// @ts-strict-ignore
import { getAbsoluteApiUrl } from "@dashboard/config";
import useNotifier from "@dashboard/hooks/useNotifier";
import { createFetch } from "@saleor/sdk";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const sdkFetch = createFetch();

export interface ZoneConfigData {
  name: string;
  countries: string[];
  volumetric_formula: string;
  max_package_height_cm: number | null;
  max_package_width_cm: number | null;
  max_package_length_cm: number | null;
  max_package_volume_cm3: number | null;
}

interface OverviewData {
  zones: ZoneConfigData[];
}

function getBackendOrigin(): string {
  try {
    return new URL(getAbsoluteApiUrl()).origin;
  } catch {
    return "";
  }
}

function getOverviewUrl(): string {
  return `${getBackendOrigin()}/shipping-overview/`;
}

function getPatchUrl(): string {
  return `${getBackendOrigin()}/shipping-zone-config/`;
}

export function useShippingZoneConfig(zoneName: string) {
  const [zone, setZone] = useState<ZoneConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const notify = useNotifier();
  const intl = useIntl();

  useEffect(() => {
    if (!zoneName) return;

    setLoading(true);
    setError(null);
    sdkFetch(getOverviewUrl(), {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    } as RequestInit)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);

        return r.json() as Promise<OverviewData>;
      })
      .then(data => {
        const found = data.zones.find(z => z.name === zoneName) ?? null;

        setZone(found);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [zoneName]);

  const save = async (patch: Partial<ZoneConfigData>) => {
    setSaving(true);

    try {
      const res = await sdkFetch(getPatchUrl(), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "include",
        body: JSON.stringify({ zone_name: zoneName, patch }),
      } as RequestInit);

      if (!res.ok) {
        const text = await res.text().catch(() => "");

        notify({
          status: "error",
          text: intl.formatMessage(
            { id: "2i02Gn", defaultMessage: "Failed to save ({status}): {detail}" },
            { status: res.status, detail: text || res.statusText },
          ),
        });

        return;
      }

      // Optimistically update local state
      setZone(prev => (prev ? { ...prev, ...patch } : prev));
      notify({
        status: "success",
        text: intl.formatMessage({
          id: "M2XjxQ",
          defaultMessage: "Shipping zone settings saved.",
        }),
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);

      notify({ status: "error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  return { zone, loading, error, saving, save };
}
